import { FC, useCallback, useState } from "react";
import { PageHeader } from "@hostingbv/lib/src/components/content/PageHeader";
import { ContentContainer } from "@hostingbv/lib/src/components/content/ContentContainer";
import { Breadcrumbs } from "@hostingbv/lib/src/components/content/Breadcrumbs";
import { SectionHeader } from "@hostingbv/lib/src/components/content/SectionHeader";
import { PageIconHeader } from "@hostingbv/lib/src/components/PageIconHeader";
import { faRocket, faShop } from "@fortawesome/free-solid-svg-icons";
import { useFinalizeCertificate, } from "@hostingbv/lib/src/api/partner/PartnerHooks";
import { Button } from "@hostingbv/lib/src/components/form/Button";
import { Callout } from "@hostingbv/lib/src/components/content/Callout";
import { ValidationError } from "@hostingbv/lib/src/api/ModelFactory";
import { Paragraph } from "@hostingbv/lib/src/components/Paragraph";
import { useRealtimeDeployments } from "../components/deployments/RealtimeDeployments";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Certificate } from "@hostingbv/lib/src/api/partner/models/Certificate";

export const CertificateFinalize: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const certificate = (location.state as { certificate?: Certificate } | null)?.certificate ?? null;

  // Every hook runs unconditionally, before the missing-certificate guard below.
  const { awaitNextDeploymentFinished } = useRealtimeDeployments()!;
  const { mutate: finalizeCertificate, error: finalizeError } = useFinalizeCertificate();
  const [finalizeFailure, setFinalizeFailure] = useState<string | null>(null);

  const certificateId = certificate?.id;
  const finalize = useCallback(async () => {
    if (certificateId === undefined) return;
    setFinalizeFailure(null);
    const deployment = await finalizeCertificate(certificateId);
    const finished = deployment.status === 'pending' || deployment.status === 'running'
      ? await awaitNextDeploymentFinished(deployment.id)
      : deployment;
    if (finished?.status === 'failed') {
      const output = finished.output as { user_error?: string } | null;
      setFinalizeFailure(output?.user_error ?? 'Failed to finalize certificate.');
      return;
    }
    navigate("/certificates/" + certificateId);
  }, [finalizeCertificate, awaitNextDeploymentFinished, navigate, certificateId]);

  // Redirecting by rendering, rather than calling navigate() during render.
  if (!certificate) return <Navigate to="/certificates" replace />;

  return (
    <ContentContainer>
      <PageIconHeader icon={faShop}>
        <Breadcrumbs crumbs={[
          { label: "Certificates", href: "/certificates" },
        ]} currentPage={"Add new certificate"} />
        <PageHeader>Add new certificate</PageHeader>
      </PageIconHeader>
      {finalizeError instanceof ValidationError ? <Callout color={"danger"}>{finalizeError.message}</Callout> : <></>}
      {finalizeFailure ? <Callout color={"danger"}>{finalizeFailure}</Callout> : <></>}
      {certificate.challengeType === 'dns-01' &&
        <Callout>
          <SectionHeader>Add validation records</SectionHeader>
          <Paragraph>
            Add the following dns records to your domain(s), then click continue.
          </Paragraph>
          {certificate.challenges.map(c =>
            <Paragraph key={c.domain + c.value}>
              _acme-challenge.{c.domain}. 60 IN TXT {c.value}
            </Paragraph>
          )}
        </Callout>
      }
      {certificate.challengeType === 'http-01' &&
        <Callout>
          <SectionHeader>Ready to go</SectionHeader>
          <Paragraph>
            Click continue to add the tls certificate, make
            sure your dns records are correctly setup before
            continuing.
          </Paragraph>
        </Callout>
      }
      <Button type={'primary'} size={'sm'} onClick={finalize} icon={faRocket} text={'Finalize certificate'} />
    </ContentContainer>
  );
}
